import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PcoAppSettings = () => {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ client_id: "", client_secret: "" });
  const [busy, setBusy] = useState(false);
  const callback = s?.redirect_uri || "Not configured on the server";
  useEffect(() => { api.get("/admin/pco-app").then((r) => setS(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/pco-app", f);
      setS(data);
      setF({ client_id: "", client_secret: "" });
      toast.success("Planning Center app credentials saved securely");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try { setS((await api.delete("/admin/pco-app")).data); toast.success("Now using the server's Planning Center app"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not reset"); }
    finally { setBusy(false); }
  };

  return (
    <section className="card-soft p-7 mt-10" data-testid="pco-app-settings">
      <div className="flex items-center justify-between gap-4">
        <p className="font-display text-navy text-lg font-semibold flex items-center gap-2"><ShieldCheck size={18} className="text-teal" /> Planning Center app credentials</p>
        {s && <span className={`text-xs rounded-full px-3 py-1 ${s.configured ? "bg-teal text-white" : "bg-paper text-muted-ink"}`} data-testid="pco-app-status">
          {s.configured ? `In use · ID …${s.client_id_hint} (${s.source === "dashboard" ? "saved here" : "server"})` : "Not configured"}</span>}
      </div>
      {s?.source === "dashboard" && s.server_client_hint && (
        <div className="bg-amber-50 text-amber-900 rounded-xl p-4 mt-5 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="pco-app-override-note">
          <span>Credentials saved here (…{s.client_id_hint}) override the server app (…{s.server_client_hint}). Make sure this app has the callback URL below registered.</span>
          <button type="button" disabled={busy} onClick={reset} className="chip-btn" data-testid="pco-app-reset-btn">Use server app instead</button>
        </div>
      )}
      <p className="text-sm text-muted-ink mt-2">Stored encrypted on the server and never sent to browsers or volunteers. Only platform admins see this.</p>
      <div className="bg-paper rounded-xl p-4 mt-5">
        <p className="text-xs uppercase tracking-[0.12em] text-muted-ink">Authorization callback URL (paste into Planning Center)</p>
        <button type="button" onClick={() => { navigator.clipboard.writeText(callback); toast.success("Callback URL copied"); }}
          className="font-mono-dt text-sm text-navy mt-2 flex items-center gap-2 hover:text-teal break-all text-left" data-testid="pco-callback-url">{callback} <Copy size={14} className="shrink-0" /></button>
      </div>
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mt-5">
        <div className="space-y-2">
          <Label>Client ID</Label>
          <Input required value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className="h-11 font-mono-dt" data-testid="pco-app-client-id-input" />
        </div>
        <div className="space-y-2">
          <Label>Client Secret</Label>
          <Input required type="password" autoComplete="off" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} className="h-11 font-mono-dt" data-testid="pco-app-secret-input" />
        </div>
        <button disabled={busy} className="btn-navy !py-2.5 justify-self-start" data-testid="pco-app-save-btn">{busy ? "Saving..." : s?.configured ? "Replace credentials" : "Save credentials"}</button>
      </form>
    </section>
  );
};
